import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { GlobalAppCloseDirtyReason } from "./types.js";

type DirtyEntry = { dirty: boolean; reason?: GlobalAppCloseDirtyReason };

type GlobalAppCloseContextValue = {
  dirty: boolean;
  dirtyReason: GlobalAppCloseDirtyReason | null;
  setDirtySource: (id: string, dirty: boolean, reason?: GlobalAppCloseDirtyReason) => void;
  clearDirtySource: (id: string) => void;
};

const GlobalAppCloseContext = createContext<GlobalAppCloseContextValue | null>(null);

export function GlobalAppCloseProvider({ children }: { children: ReactNode }) {
  const mapRef = useRef<Map<string, DirtyEntry>>(new Map());
  const [version, setVersion] = useState(0);

  const setDirtySource = useCallback((id: string, dirty: boolean, reason?: GlobalAppCloseDirtyReason) => {
    const prev = mapRef.current.get(id);
    if (prev && prev.dirty === dirty && prev.reason === reason) return;
    if (!dirty) mapRef.current.delete(id);
    else mapRef.current.set(id, { dirty: true, ...(reason ? { reason } : {}) });
    setVersion((v) => v + 1);
  }, []);

  const clearDirtySource = useCallback((id: string) => {
    if (!mapRef.current.has(id)) return;
    mapRef.current.delete(id);
    setVersion((v) => v + 1);
  }, []);

  const value = useMemo(() => {
    void version;
    let dirty = false;
    let dirtyReason: GlobalAppCloseDirtyReason | null = null;
    for (const entry of mapRef.current.values()) {
      if (entry.dirty) {
        dirty = true;
        dirtyReason = entry.reason ?? dirtyReason;
      }
    }
    return { dirty, dirtyReason, setDirtySource, clearDirtySource };
  }, [version, setDirtySource, clearDirtySource]);

  return <GlobalAppCloseContext.Provider value={value}>{children}</GlobalAppCloseContext.Provider>;
}

export function useGlobalAppClose(): GlobalAppCloseContextValue {
  const ctx = useContext(GlobalAppCloseContext);
  if (!ctx) {
    return {
      dirty: false,
      dirtyReason: null,
      setDirtySource: () => undefined,
      clearDirtySource: () => undefined,
    };
  }
  return ctx;
}

/** Register a named dirty source; clears on unmount. */
export function useRegisterGlobalAppCloseDirty(
  id: string,
  dirty: boolean,
  reason?: GlobalAppCloseDirtyReason,
): void {
  const { setDirtySource, clearDirtySource } = useGlobalAppClose();
  useEffect(() => {
    setDirtySource(id, dirty, reason);
    return () => clearDirtySource(id);
  }, [id, dirty, reason, setDirtySource, clearDirtySource]);
}
